import { useEffect, useState } from 'react';
import { CheckCircleOutlined, CopyOutlined, IdcardOutlined, MobileOutlined, PlusOutlined, ReloadOutlined, SafetyCertificateOutlined, SearchOutlined, ShopOutlined, StopOutlined } from '@ant-design/icons';
import { App, Button, Drawer, Form, Input, Segmented, Space } from 'antd';
import { api } from '../api';
import { ActiveTag, DataTable, ErrorNotice, PageHeader, Panel, Person, RowActions } from '../components';
import { countLabel, formatDate, MOBILE_RULES, mobileLabel, useLoad } from '../lib';
import { DealerPanel, money, useGrants, WalletPanel } from './accountPanels';

function WalletDrawer({ account, onClose, onChanged }) {
    return (<Drawer title={`${account?.name ?? ''} · Kivonex Wallet`} open={Boolean(account)} onClose={onClose} size={900} destroyOnHidden>
      {account && <WalletPanel account={account} onChanged={onChanged}/>}
    </Drawer>);
}
function DealerDrawer({ account, onClose, onChanged }) {
    return (<Drawer title={`${account?.name ?? ''} · Dealer`} open={Boolean(account)} onClose={onClose} size={860} destroyOnHidden>
      {account && <DealerPanel account={account} onChanged={onChanged}/>}
    </Drawer>);
}
/** What is searched for: a number typed as "+91 98765 43210" is searched as its digits, as the API keeps them. */
function searchTerm(text) {
    const value = text.trim();
    if (!/^[+\d][\d\s-]*$/.test(value))
        return value;
    const digits = value.replace(/\D/g, '');
    return digits.length > 10 && digits.startsWith('91') ? digits.slice(2) : digits;
}
export function AccountsPage({ go }) {
    const { message } = App.useApp();
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [filter, setFilter] = useState('all');
    const [open, setOpen] = useState(false);
    const [walletAccount, setWalletAccount] = useState(null);
    const [dealerAccount, setDealerAccount] = useState(null);
    const [saving, setSaving] = useState(false);
    const [form] = Form.useForm();
    // Search as the admin types, once they pause.
    useEffect(() => {
        const timer = window.setTimeout(() => setQuery(searchTerm(search)), 300);
        return () => window.clearTimeout(timer);
    }, [search]);
    const { data, error, loading, reload } = useLoad(() => api(`/v1/admin/accounts?q=${encodeURIComponent(query)}`).then((body) => body.accounts), [query]);
    const accounts = data ?? [];
    const grants = useGrants(reload);
    const openProfile = (row) => go('accounts', row.id);
    const shown = accounts.filter((row) => (filter === 'all' ? true : filter === 'active' ? !row.disabledAt : filter === 'dealers' ? Boolean(row.dealer?.active) : Boolean(row.disabledAt)));
    const dealerCount = accounts.filter((row) => row.dealer?.active).length;
    const activeCount = accounts.filter((row) => !row.disabledAt).length;
    async function toggle(row) {
        try {
            await api(`/v1/admin/accounts/${row.id}/${row.disabledAt ? 'enable' : 'disable'}`, { method: 'POST' });
            message.success(row.disabledAt ? `${row.name} can sign in again` : `${row.name} is disabled`);
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not change the account');
        }
    }
    async function copy(text, what) {
        try {
            await navigator.clipboard.writeText(text);
            message.success(`${what} copied`);
        }
        catch {
            message.error(`Could not copy the ${what.toLowerCase()}`);
        }
    }
    async function create(values) {
        setSaving(true);
        try {
            await api('/v1/admin/accounts', { method: 'POST', body: JSON.stringify({ name: values.name, tradeName: values.tradeName?.trim() || undefined, mobile: values.mobile, email: values.email?.trim() || undefined }) });
            message.success(`Account created for ${values.name}`);
            setOpen(false);
            form.resetFields();
            await reload();
        }
        catch (err) {
            message.error(err instanceof Error ? err.message : 'Could not create the account');
        }
        finally {
            setSaving(false);
        }
    }
    return (<>
      <PageHeader title="Accounts" description="Customers sign in with their mobile number and a one-time code, in the account panel and in the desktop app. Most create their own account there." actions={<Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            New account
          </Button>}/>
      <ErrorNotice error={error}/>

      <Panel flush>
        <div className="toolbar">
          <Input allowClear prefix={<SearchOutlined style={{ color: 'var(--muted)' }}/>} placeholder="Search by name, mobile or email" value={search} onChange={(event) => setSearch(event.target.value)} style={{ width: 320, maxWidth: '100%' }}/>
          <Segmented value={filter} onChange={setFilter} options={[
            { value: 'all', label: countLabel('All', accounts.length, data !== null) },
            { value: 'active', label: countLabel('Active', activeCount, data !== null) },
            { value: 'disabled', label: countLabel('Disabled', accounts.length - activeCount, data !== null) },
            { value: 'dealers', label: countLabel('Dealers', dealerCount, data !== null) }
        ]}/>
        </div>
        <DataTable rowKey="id" loading={loading} dataSource={shown} pagination={shown.length > 20 ? { pageSize: 20, showSizeChanger: false } : false} scroll={{ x: 1170 }} locale={{ emptyText: query || filter !== 'all' ? 'No accounts match.' : 'No accounts yet. Create the first one.' }} columns={[
            {
                title: 'Account',
                skeleton: 'person',
                render: (_value, row) => (<button type="button" className="account-link" onClick={() => openProfile(row)} aria-label={`Open ${row.name}'s profile`}>
                    <Person name={row.name} detail={<span className="num">{row.tradeName || mobileLabel(row.mobile) || 'No mobile'}</span>}/>
                  </button>)
            },
            { title: 'Email', dataIndex: 'email', width: 220, render: (value) => value || <span className="cell-sub">—</span> },
            {
                title: 'Billing',
                width: 220,
                render: (_value, row) => row.billing?.name || row.billing?.gstin ? (<span>
                    {row.billing.name || row.name}
                    {row.billing.gstin && <span className="cell-sub mono">GSTIN {row.billing.gstin}</span>}
                  </span>) : (<span className="cell-sub">—</span>)
            },
            { title: 'Created', dataIndex: 'createdAt', width: 150, render: (value) => formatDate(value) },
            { title: 'Wallet', width: 140, render: (_value, row) => <Button type="link" onClick={() => setWalletAccount(row)}>{money(row.wallet?.availablePaise)}</Button> },
            {
                title: 'Dealer',
                width: 170,
                render: (_value, row) => row.dealer?.active
                    ? <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => setDealerAccount(row)}><span className="num">A {row.dealer.balances.activation} · R {row.dealer.balances.renewal}</span></Button>
                    : <span className="cell-sub">—</span>
            },
            { title: 'Status', dataIndex: 'disabledAt', width: 130, skeleton: 'tag', render: (value) => <ActiveTag active={!value}/> },
            {
                title: '',
                width: 56,
                align: 'right',
                skeleton: 'actions',
                render: (_value, row) => (<RowActions label={`Actions for ${row.name}`} actions={[
                        { key: 'profile', icon: <IdcardOutlined />, label: 'Open profile', onClick: () => openProfile(row) },
                        !row.disabledAt && { key: 'activate', icon: <SafetyCertificateOutlined />, label: 'Activate', hint: 'Lifetime use, with the included renewal. No payment', onClick: () => grants.activate(row) },
                        !row.disabledAt && { key: 'renew', icon: <ReloadOutlined />, label: 'Renew', hint: 'Add years of renewal to an activated account', onClick: () => grants.renew(row) },
                        Boolean(row.mobile) && { key: 'copy-mobile', icon: <CopyOutlined />, label: 'Copy mobile', onClick: () => copy(mobileLabel(row.mobile), 'Mobile') },
                        { key: 'dealer', icon: <ShopOutlined />, label: row.dealer?.active ? 'Dealer credits' : 'Make dealer', hint: 'Dealer setting and its Activation / Renewal credits', onClick: () => setDealerAccount(row) },
                        Boolean(row.email) && { key: 'copy-email', icon: <CopyOutlined />, label: 'Copy email', onClick: () => copy(row.email ?? '', 'Email') },
                        row.disabledAt
                            ? { key: 'enable', icon: <CheckCircleOutlined />, label: 'Enable account', hint: 'They can sign in again', onClick: () => toggle(row) }
                            : {
                                key: 'disable',
                                icon: <StopOutlined />,
                                label: 'Disable account',
                                danger: true,
                                hint: 'Signs out every computer and blocks sign-in',
                                confirm: {
                                    title: 'Disable this account?',
                                    description: `${row.name} will not be able to sign in, in the account panel or the desktop app, until it is enabled again.`,
                                    okText: 'Disable'
                                },
                                onClick: () => toggle(row)
                            }
                    ]}/>)
            }
        ]}/>
      </Panel>

      <Drawer title="New account" open={open} onClose={() => setOpen(false)} size={440} destroyOnHidden footer={<Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="primary" loading={saving} onClick={() => form.submit()}>
              Create account
            </Button>
          </Space>}>
        <Form form={form} layout="vertical" requiredMark="optional" onFinish={create}>
          <Form.Item label="Full name" name="name" rules={[{ required: true, message: 'Enter the name.' }]}>
            <Input placeholder="Asha Mehta" autoFocus/>
          </Form.Item>
          <Form.Item label="Trade name" name="tradeName" extra="The business name. Shown on invoices until they add billing details.">
            <Input placeholder="Asha Traders"/>
          </Form.Item>
          <Form.Item label="Mobile number" name="mobile" rules={MOBILE_RULES} extra="They sign in with this number and a one-time code sent to it. No password.">
            <Input prefix={<MobileOutlined style={{ color: 'var(--muted)' }}/>} placeholder="98765 43210" inputMode="tel" autoComplete="off" maxLength={16}/>
          </Form.Item>
          <Form.Item label="Email" name="email" rules={[{ type: 'email', message: 'Enter a valid email.' }]} extra="For receipts and your records.">
            <Input placeholder="asha@company.com" autoComplete="off"/>
          </Form.Item>
        </Form>
      </Drawer>
      <WalletDrawer account={walletAccount} onClose={() => setWalletAccount(null)} onChanged={reload}/>
      <DealerDrawer account={dealerAccount} onClose={() => setDealerAccount(null)} onChanged={reload}/>
      {grants.dialog}
    </>);
}
